import type { DocNode } from '@atlaskit/adf-schema';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import {
	createEndAIExperienceCommand,
	createEndAISessionCommand,
	createOpenAIModalCommand,
	createStartAISessionCommand,
	createUpdateTargetIdCommand,
} from '../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../types/types';
import type { EditorPluginAIConfigItem } from '../ui/prebuilt/config-items/config-items';
import { type InvokedFrom } from '../ui/utils/analytics/analyticsFlowTypes';

export const openAIModal = ({
	invokedFrom,
	aiOptIn,
	configItem,
	editorView,
	triggeredFor,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
}: {
	aiOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItem;
	editorView?: EditorView;
	hideLoadingScreen?: boolean;
	invokedFrom: InvokedFrom;
	isExternal?: boolean;
	triggeredFor?: NodeType;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
}) => {
	if (!editorView) {
		return;
	}

	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		lastTriggeredFrom: invokedFrom,
		aiGlobalOptIn: aiOptIn,
		triggeredFor,
		isExternal,
		withAcceptReject,
		withStreamingHighlight,
		hideLoadingScreen,
	});

	openAIModalCommand(editorView.state, editorView.dispatch);
};

export const endAIExperience = ({ editorView }: { editorView?: EditorView }) => {
	if (!editorView) {
		return;
	}

	const endAIExperienceCommand = createEndAIExperienceCommand();
	endAIExperienceCommand(editorView.state, editorView.dispatch);
};

export const startAISession = ({
	editorView,
	sessionId,
}: {
	editorView?: EditorView;
	sessionId: string;
}) => {
	if (!editorView) {
		return;
	}

	return createStartAISessionCommand(sessionId)(editorView.state, editorView.dispatch);
};

export const endAISession = ({
	editorView,
	sessionId,
}: {
	editorView?: EditorView;
	sessionId: string;
}) => {
	if (!editorView) {
		return;
	}
	return createEndAISessionCommand(sessionId)(editorView.state, editorView.dispatch);
};

export const updateTargetId = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string | number;
}) => {
	if (!editorView) {
		return;
	}
	return createUpdateTargetIdCommand(targetId)(editorView.state, editorView.dispatch);
};

interface GetPartialPMNodeFromSelectionOptions {
	maxSize?: number;
	selection: Selection | undefined;
}

export const getPartialPMNodeFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => PMNode | undefined = ({ selection, maxSize = 10000 }) => {
	if (!selection) {
		return undefined;
	}

	const { $from, $to } = selection;

	const doc = $from.doc;

	/**
	 * Check the from and to positions of the lowest depth level (doc level).
	 * If the size is over the threshold, increase the depth.
	 */

	let depth = 0;

	while (
		// Check until max depth is reached from the resolved positions
		!isNaN($from.start(depth)) &&
		!isNaN($to.end(depth))
	) {
		const from = $from.start(depth);
		const to = $to.end(depth);
		if (to - from < maxSize) {
			break;
		}
		depth++;
	}

	if (depth === 0) {
		return doc;
	}

	// find a depth that fits and then start looking at siblings n - 1 and n + 1 and so on

	const parentDepth = depth - 1;
	const index = $from.index(parentDepth);

	let from = $from.start(depth);
	let to = $to.end(depth);
	let increment = 1;

	const maxIndex = $from.node(parentDepth).childCount;

	while (
		// Loop until both ends of the bounds have been reached
		index - increment >= 0 ||
		index + increment <= maxIndex
	) {
		const newFrom = $from.posAtIndex(Math.max(index - increment, 0), parentDepth);
		// We look ahead at the next index as posAtIndex yields the starting position of the node
		const newTo = $to.posAtIndex(Math.min(index + increment + 1, maxIndex), parentDepth);

		let isPositionUpdated = false;
		if (to - newFrom < maxSize) {
			from = newFrom;
			isPositionUpdated = true;
		}
		if (newTo - from < maxSize) {
			to = newTo;
			isPositionUpdated = true;
		}

		if (!isPositionUpdated) {
			break;
		}
		increment++;
	}

	return doc.cut(from, to);
};

export const getPartialDocumentFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => DocNode | undefined = (options) => {
	const transformer = new JSONTransformer();
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return transformer.encode(doc) as DocNode;
	}
};

export const getPartialMarkdownFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => string | undefined = (options) => {
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return convertProsemirrorNodeToMarkdown({ node: doc })?.markdown ?? '';
	}
};
